import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { LexicalEditor, TextNode } from 'lexical';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

interface TypeaheadProps<T> {
  renderItem: (item: T, queryString: string | null) => ReactElement;
  selectItem: (
    item: T,
    editor: LexicalEditor,
    queryString: string | null,
  ) => void;
  trigger: string;
  options: T[];
}

interface SelectableOption extends MenuOption {
  keywords: string[];
  value: string | number;
  onSelect?: () => void;
  alwaysShow?: boolean;
}

export default function TypeaheadPlugin<T extends SelectableOption>({
  renderItem,
  selectItem,
  trigger,
  options,
}: TypeaheadProps<T>): React.JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);

  const checkForTriggerMatch = useBasicTypeaheadTriggerMatch(trigger, {
    allowWhitespace: true,
    minLength: 0,
  });

  const filterredOptions = useMemo(() => {
    if (!queryString) {
      return options;
    }

    const regex = new RegExp(queryString, 'i');

    return options.filter(
      (option) =>
        option.alwaysShow ||
        regex.test(option.value.toString()) ||
        option.keywords.some((keyword) => regex.test(keyword)),
    );
  }, [queryString, options]);

  const onSelectOption = useCallback(
    (
      selectedOption: T,
      nodeToRemove: TextNode | null,
      closeMenu: () => void,
    ) => {
      editor.update(() => {
        nodeToRemove?.remove();
      });

      if (selectItem) {
        selectItem(selectedOption, editor, queryString);
      } else if (selectedOption.onSelect) {
        selectedOption.onSelect();
      }

      closeMenu();
    },
    [editor, selectItem, queryString],
  );

  return (
    <>
      <LexicalTypeaheadMenuPlugin<T>
        onQueryChange={setQueryString}
        onSelectOption={onSelectOption}
        triggerFn={checkForTriggerMatch}
        options={filterredOptions}
        menuRenderFn={(
          anchorElementRef,
          {
            selectedIndex,
            options,
            selectOptionAndCleanUp,
            setHighlightedIndex,
          },
        ) => {
          return anchorElementRef.current
            ? createPortal(
                <div className="bg-popover text-popover-foreground z-50 w-64 rounded-md border p-1 shadow-md">
                  <ul className="max-h-[300px] overflow-x-hidden overflow-y-auto">
                    {options.map((option, i: number) => (
                      <ComponentPickerMenuItem<T>
                        index={i}
                        isSelected={selectedIndex === i}
                        onClick={() => {
                          setHighlightedIndex(i);
                          selectOptionAndCleanUp(option);
                        }}
                        onMouseEnter={() => {
                          setHighlightedIndex(i);
                        }}
                        key={option.key}
                        option={option}
                        renderItem={renderItem}
                        queryString={queryString}
                      />
                    ))}
                  </ul>
                </div>,

                anchorElementRef.current,
              )
            : null;
        }}
      />
    </>
  );
}

function ComponentPickerMenuItem<T extends SelectableOption>({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
  renderItem,
  queryString,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: T;
  renderItem: (item: T, queryString: string | null) => ReactElement;
  queryString: string | null;
}) {
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={`relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm
        outline-hidden select-none ${
        isSelected
            ? 'bg-accent text-accent-foreground'
            : 'hover:bg-accent hover:text-accent-foreground'
        }`}
      role="option"
      aria-selected={isSelected}
      id={'typeahead-item-' + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      {renderItem(option, queryString)}
    </li>
  );
}
